/**
 * Settings (`/settings/:section`) — a grouped section nav and one pane per section.
 *
 * HOF-627 settings refactor: the page used to be one long scroll whose farm sections hid behind
 * "Anzeigen" toggles. It is now the two-column settings surface from the design system
 * (`SettingsLayout`, see its story `shadcn/Settings`): every section deep-linkable, the current
 * one marked in the nav, focus moved to the pane heading on a switch, and a drill-in list on
 * narrow screens (`/settings` shows the nav; `/settings/<id>` the pane with a way back).
 *
 * Which sections show follows the person's seat in the active farm (HOF-627 §4) — the same
 * reading as before, now deciding nav items instead of page sections. A pane mounts only while
 * open, so its reads (team, farm, own organisations) run only for someone looking at it; the
 * first paint of `/settings` makes no request.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { SettingsLayout, SettingsNav, SettingsNavGroup, SettingsNavItem } from '@hofsync/design-system';
import { useAuth } from '../contexts/AuthContext';
import ProfilePane from './settings/ProfilePane';
import AccountPane from './settings/AccountPane';
import EmailDelegationPane from './settings/EmailDelegationPane';
import OwnOrganisationsSection from './settings/OwnOrganisationsSection';
import TeamSection from './settings/TeamSection';
import FarmSection from './settings/FarmSection';
import FarmParametersPane from './settings/FarmParametersPane';
import { PARAMETER_READ_PERMISSIONS } from './settings/farmParameters';
import { EMPTY_OWN_ORG_OVERLAY, type OwnOrgOverlay } from './settings/ownOrganisations';
import {
    DEFAULT_SECTION,
    SETTINGS_GROUPS,
    SETTINGS_SECTIONS,
    settingsPath,
    type SettingsSectionId,
} from './settings/sections';

const SettingsPage: React.FC = () => {
    const { t } = useTranslation();
    const { userProfile } = useAuth();
    const { section } = useParams<{ section?: string }>();
    const [ownOrgOverlay, setOwnOrgOverlay] = useState<OwnOrgOverlay>(EMPTY_OWN_ORG_OVERLAY);

    const isFarmer = userProfile?.account_type === 'farmer';
    // HOF-627 §4: what this person may see follows their seat in the active farm. A profile
    // from before the seat existed (no `active_farm`) keeps the legacy owner reading.
    // An ABSENT ``active_farm`` is a profile from before seats (legacy ``account_type`` decides);
    // ``null`` is the seat model saying "no farm" and grants nothing (codex round 3).
    const seatModel = userProfile?.active_farm !== undefined;
    const activeFarm = userProfile?.active_farm ?? null;
    const farms = userProfile?.farms ?? [];
    const farmMay = (permission: string) =>
        seatModel ? !!activeFarm?.permissions.includes(permission) : isFarmer;
    const canReadMembers = farmMay('members.read');
    // The invite form, the e-mail delegation and the own-organisation editor act on the session's
    // ACTIVE farm (BFF ``effective_user_id`` → the backend's farm principal), so they follow that
    // seat's permission — whoever holds it, founder or not (release gate C2; it replaced the
    // own-farm rule of codex P2, written while these routes still acted on the person's own farm).
    // Each gate is the permission its backend route enforces: grant administration is
    // ``members.manage`` (``integrations.manage`` is what a grant confers), the ``internal`` flag
    // ``twin.write.protected``, the invite routes ``members.invite``.
    const canLegacyInvite = farmMay('members.invite');
    const canDelegate = farmMay('members.manage');
    const canEditOwnOrganisations = farmMay('twin.write.protected');
    // HOF-708: Betriebsparameter — the farm's own settings, for any seat that may read one of them
    // (the registry's read permissions, ``PARAMETER_READ_PERMISSIONS``); each editor knows if it may write.
    const canReadParameters = seatModel && PARAMETER_READ_PERMISSIONS.some((p) => farmMay(p));

    const available = new Set<SettingsSectionId>(['profile']);
    // Betrieb also with no active farm while the person has another to choose (codex round 11:
    // the farm they worked in entered deletion — the switcher is their way out).
    const hasFarmPane = !!activeFarm || farms.length > 0;
    // Deleting the account closes the Betrieb pane; Konto exists only for someone without one.
    available.add(hasFarmPane ? 'farm' : 'account');
    if (canReadParameters) available.add('parameters');
    if (canReadMembers || canLegacyInvite) available.add('team');
    if (canDelegate) available.add('email');
    if (canEditOwnOrganisations) available.add('organisations');

    // An old /settings/account link lands where the account is deleted now.
    const requested = (section === 'account' && hasFarmPane ? 'farm' : section) as SettingsSectionId | undefined;
    const active: SettingsSectionId = requested && available.has(requested) ? requested : DEFAULT_SECTION;
    const activeDef = SETTINGS_SECTIONS.find((s) => s.id === active)!;
    const settingsTitle = t('settings.title', 'Einstellungen');

    // Focus moves to the pane heading on a pane SWITCH, never on the page's first paint.
    const shownBefore = useRef(false);
    const focusOnMount = shownBefore.current;
    useEffect(() => {
        shownBefore.current = true;
    });

    useEffect(() => {
        const previous = document.title;
        document.title = `${t(activeDef.labelKey, activeDef.fallback)} – ${settingsTitle} – Hofsync`;
        return () => {
            document.title = previous;
        };
    }, [activeDef, settingsTitle, t]);

    const chrome = {
        focusOnMount,
        backLink: (
            <Link to="/settings">
                <ChevronLeft aria-hidden="true" size={16} /> {settingsTitle}
            </Link>
        ),
    };

    const nav = (
        <SettingsNav label={settingsTitle} heading={settingsTitle}>
            {SETTINGS_GROUPS.map((group) => {
                const items = SETTINGS_SECTIONS.filter((s) => s.group === group.id && available.has(s.id));
                if (items.length === 0) return null;
                return (
                    <SettingsNavGroup key={group.id} label={t(group.labelKey, group.fallback)}>
                        {items.map((item) => (
                            <SettingsNavItem key={item.id} active={item.id === active} icon={<item.icon size={16} />} asChild>
                                <Link to={settingsPath(item.id)}>{t(item.labelKey, item.fallback)}</Link>
                            </SettingsNavItem>
                        ))}
                    </SettingsNavGroup>
                );
            })}
        </SettingsNav>
    );

    return (
        <SettingsLayout nav={nav} mobileView={requested ? 'pane' : 'nav'}>
            {active === 'profile' && <ProfilePane {...chrome} />}
            {active === 'account' && <AccountPane {...chrome} />}
            {active === 'farm' && (
                <FarmSection
                    {...chrome}
                    farms={farms}
                    activeTenantId={activeFarm?.tenant_id ?? null}
                    onSwitched={() => window.location.reload()}
                />
            )}
            {active === 'parameters' && <FarmParametersPane {...chrome} />}
            {active === 'team' && <TeamSection {...chrome} members={canReadMembers} legacyInvites={canLegacyInvite} />}
            {active === 'email' && <EmailDelegationPane {...chrome} />}
            {active === 'organisations' && (
                <OwnOrganisationsSection {...chrome} overlay={ownOrgOverlay} setOverlay={setOwnOrgOverlay} />
            )}
        </SettingsLayout>
    );
};

export default SettingsPage;
